import { useContext, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import RichTextEditor from 'react-rte';
import parse from 'html-react-parser';
import {
  animationScrollToBottom,
  animationScrollToTop,
  convertHtmlToString,
  convertMessageToHtmlAndTrimSpaces,
  getSenderName,
  isJustNowMessageCheck,
  shortenFilename
} from '../utils';
import { ReactComponent as DownloadIcon } from 'assets/LoadCircle.svg';
import i18nContext from 'components/i18n-context';
import { MAX_MESSAGE_LENGTH, MAX_UPLOADED_FILES_SIZE } from 'components/common/constants';
import Loader from 'components/common/Loader';
import { parseDate } from 'components/utils';
import { convertBytesToMegabytes, getErrorMessageForAlert, messageRegExp, prepareFieldErrors } from 'services/utils';
import Button from 'uikit/Button/Button';
import { IconButton } from 'uikit/IconButton/IconButton';
import Input from 'uikit/Input/Input';
import { AttachDoc } from 'uikit/AttachDoc/AttachDoc';
import '../Messages.scss';

const Chat = ({
  topic,
  messages,
  userAccount,
  isLoading,
  error,
  clearError,
  generateSecurityCode,
  setNewMessageData,
  uploadedFiles,
  uploadDocuments,
  removeAttachment,
  getCurrentTopicMessagesFilesUrls,
  downloadAttachment,
  topicsRef,
  canWriteMessages,
  clearUploadedFiles,
  isNewMessageCreated,
  setIsNewMessageCreated,
  isShowingInternalActionConfirmation
}) => {
  const i18n = useContext(i18nContext);
  const { id } = useParams();
  const serverFieldsErrors = prepareFieldErrors(i18n, error);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [hasScroll, setHasScroll] = useState(false);
  const chatRef = useRef(null);
  const [messageValue, setMessageValue] = useState(RichTextEditor.createEmptyValue());

  useEffect(() => {
    getCurrentTopicMessagesFilesUrls();

    if (chatRef) {
      const chat = chatRef.current;
      if (chat && chat.scrollHeight > chat.clientHeight) {
        setHasScroll(true);
      } else {
        setHasScroll(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (chatRef.current) {
        chatRef.current.scrollTop = chatRef.current.scrollHeight;
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [messages]);

  const form = useFormik({
    validateOnChange: false,
    initialValues: {
      message: ''
    },
    validationSchema: Yup.object({
      message: Yup.string()
        .required(i18n.getMessage('messages.error.message.empty'))
        .max(MAX_MESSAGE_LENGTH, i18n.getMessage('messages.error.message.maxSize', { size: MAX_MESSAGE_LENGTH }))
        .matches(messageRegExp, i18n.getMessage('messages.error.message.invalidFormat'))
    }),
    onSubmit: () => {
      setNewMessageData(convertMessageToHtmlAndTrimSpaces(messageValue));
      generateSecurityCode(topic.id);
    }
  });

  const { values, errors, handleSubmit, validateField, submitCount, setFieldValue, resetForm } = form;

  useEffect(() => {
    if (id) {
      resetForm();
      setMessageValue(RichTextEditor.createEmptyValue());
      clearUploadedFiles();
    }
  }, [id]);

  useEffect(() => {
    setFieldValue('message', convertHtmlToString(messageValue));
  }, [messageValue, setFieldValue]);

  const handleMouseEnter = (index) => {
    setHoveredIndex(index);
  };

  const handleMouseLeave = () => {
    setHoveredIndex(null);
  };

  const onBlurMessageField = () => {
    validateField('message');
    if (serverFieldsErrors?.message) {
      clearError();
    }
  };

  useEffect(() => {
    if (isNewMessageCreated) {
      animationScrollToBottom(chatRef);
      animationScrollToTop(topicsRef);
      setHoveredIndex(null);
      setFieldValue('message', '');
      setMessageValue(RichTextEditor.createEmptyValue());
      resetForm();
      setIsNewMessageCreated(false);
    }
  }, [isNewMessageCreated]);

  return (
    <div className='messages-right-side'>
      {isLoading ? (
        <div className='loader-container'>
          <Loader />
        </div>
      ) : (
        <>
          <div className='chat-subject-wrapper'>
            <span className='chat-subject-text'>{topic?.subject}</span>
          </div>
          <div id='chat-messages' className='chat-messages-wrapper' ref={chatRef}>
            {messages.map((message, index) => {
              const createdAt = parseDate(message.create_at);
              const isJustNowMessageCreated = isJustNowMessageCheck(message.create_at);

              return (
                <div key={index} className='chat-message-block'>
                  <div className='chat-message-head'>
                    <span className='chat-message-author'>{getSenderName(message?.sender_type, userAccount)}</span>
                    {isJustNowMessageCreated ? (
                      <div className='chat-message-date-block'>
                        <span className='chat-message-date-just-now'>{i18n.getMessage('messages.date.justNow')}</span>
                      </div>
                    ) : (
                      <div className='chat-message-date-block'>
                        <span className='chat-message-date-text'>{createdAt.date}</span>
                        <span className='chat-message-date-text'>{createdAt.time}</span>
                      </div>
                    )}
                  </div>
                  <div className='chat-message-content'>{parse(message.body)}</div>
                  {message.documents.length > 0 && (
                    <div className='chat-message-attachments-wrapper'>
                      <span className='chat-message-attachments-label'>Attachments ({message.documents.length})</span>
                      <div className='chat-message-attachments-preview-wrapper'>
                        {message.documents.map((file, index) => {
                          return (
                            <div
                              key={index}
                              className='chat-message-attachment-wrapper'
                              onMouseEnter={() => handleMouseEnter(index)}
                              onMouseLeave={handleMouseLeave}
                            >
                              {!file.url ? (
                                <div className='chat-message-attachment'>
                                  <p className='placeholder-wave h-100'>
                                    <span className='placeholder w-100 h-100 skeleton' />
                                  </p>
                                </div>
                              ) : file.content_type === 'application/pdf' ? (
                                <embed className='chat-message-attachment' src={file.url} type='application/pdf' />
                              ) : (
                                <img className='chat-message-attachment' src={file.url} alt={file.name} />
                              )}
                              {hoveredIndex === index && (
                                <div className='overlay'>
                                  <div className='overlay-top-half'>
                                    <span className='chat-message-attachment-name'>{shortenFilename(file.name)}</span>
                                  </div>
                                  <div className='overlay-bottom-half'>
                                    <div className='overlay-icon-wrapper'>
                                      <IconButton
                                        onClick={() => downloadAttachment(file.id)}
                                        Icon={DownloadIcon}
                                        isDisabled={isLoading}
                                      />
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {canWriteMessages && (
            <form
              action='#'
              id='chat-form'
              onSubmit={handleSubmit}
              className={`chat-form ${hasScroll ? 'has-scroll' : ''}`}
            >
              <Input
                className='chat-form-input'
                type={'text-editor'}
                autoFocus={true}
                label={i18n.getMessage('messages.form.input.message.label')}
                name={'message'}
                isDisabled={isShowingInternalActionConfirmation}
                value={messageValue}
                onChange={setMessageValue}
                onBlur={onBlurMessageField}
                initialStatus={submitCount}
                max={MAX_MESSAGE_LENGTH}
                rows={4}
                error={errors?.message || serverFieldsErrors?.message}
                /* eslint-disable-next-line max-len */
                symbolsCountText={i18n.getMessage('messages.form.input.message.symbolsLeft', {
                  amount: MAX_MESSAGE_LENGTH - values.message.length
                })}
              />
              <AttachDoc
                label={i18n.getMessage('messages.form.attachDoc.label', { amount: uploadedFiles.length })}
                className='chat-form-attachments'
                type='file'
                size={MAX_UPLOADED_FILES_SIZE}
                isDisabled={isShowingInternalActionConfirmation}
                files={uploadedFiles}
                onChange={uploadDocuments}
                onRemoveFiles={removeAttachment}
                error={error?.type === 'attachDoc' && getErrorMessageForAlert(i18n, error)}
                hintText={i18n.getMessage('attachDoc.hint.text', {
                  maxFilesSize: convertBytesToMegabytes(MAX_UPLOADED_FILES_SIZE)
                })}
              />

              <Button
                className='chat-form-button'
                type={'primary'}
                roleType={'submit'}
                size={'large'}
                fullWidth={true}
                onClick={() => {}}
                isDisabled={isLoading}
              >
                {isLoading ? <Loader /> : i18n.getMessage('messages.button.sendMessage')}
              </Button>
            </form>
          )}
        </>
      )}
    </div>
  );
};

Chat.propTypes = {
  isShowingInternalActionConfirmation: PropTypes.bool.isRequired,
  topic: PropTypes.object,
  messages: PropTypes.array.isRequired,
  userAccount: PropTypes.object.isRequired,
  isLoading: PropTypes.bool.isRequired,
  error: PropTypes.any,
  clearError: PropTypes.func.isRequired,
  generateSecurityCode: PropTypes.func.isRequired,
  setNewMessageData: PropTypes.func.isRequired,
  uploadedFiles: PropTypes.array,
  uploadDocuments: PropTypes.func.isRequired,
  removeAttachment: PropTypes.func.isRequired,
  getCurrentTopicMessagesFilesUrls: PropTypes.func.isRequired,
  downloadAttachment: PropTypes.func.isRequired,
  topicsRef: PropTypes.any.isRequired,
  canWriteMessages: PropTypes.bool.isRequired,
  clearUploadedFiles: PropTypes.func.isRequired,
  isNewMessageCreated: PropTypes.bool.isRequired,
  setIsNewMessageCreated: PropTypes.func.isRequired
};

export default inject((stores) => ({
  messagesStore: stores.messagesStore
}))(observer(Chat));
